/** In-session whiteboard panel — Phase 1B-wb (frontend).
 *
 * Excalidraw canvas mounted as a full-screen overlay; the operator
 * clicks "Open whiteboard" in the toolbar to bring it up. Scene
 * patches broadcast over the LiveKit data channel so every
 * participant sees the same drawing in real time.
 *
 * Save-to-evidence: only shown for the operator (it's the operator's
 * audit trail). The button exports the canvas to PNG, serialises the
 * Excalidraw element schema, and POSTs both to
 * `/v1/sessions/{id}/evidence/whiteboard`. Combined sha256 of the
 * PNG + canonical JSON is anchored to the audit chain server-side.
 *
 * Echo prevention: each peer stamps outbound patches with its own
 * origin id and ignores patches whose origin matches. Late joiners
 * miss prior strokes (no replay at v1 — operators tend to clear
 * before introducing the whiteboard mid-session anyway).
 */
import { Excalidraw, exportToBlob } from "@excalidraw/excalidraw";
import { formatTime } from "@/lib/format";
import type {
  ExcalidrawImperativeAPI,
  ExcalidrawInitialDataState,
} from "@excalidraw/excalidraw/types";
import "@excalidraw/excalidraw/index.css";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

import { ApiPaths, api } from "@/lib/api";
import type { CommandBus } from "@/lib/commandBus";


type Props = {
  sessionId: string;
  /** The CommandBus from the LiveKit room — used for collab sync.
   *  Null when the room isn't up yet (panel still works, just no sync). */
  bus: CommandBus | null;
  /** True when this side may "Save to evidence" (operator only at v1). */
  canSave: boolean;
  onClose: () => void;
  /** Phase 1B-I — fires after a successful Save to Evidence with the
   *  new Evidence id. Lets the caller pop a CaptureMetadataModal so
   *  the operator can name the whiteboard before it sits silently in
   *  the chat. Optional — when omitted, save still works (no modal). */
  onSaved?: (evidenceId: string) => void;
};


type Patch = {
  origin: string;
  // Full elements array on every patch — simple + correct.
  // Excalidraw scenes are small (<100 KB JSON typical) so the
  // bandwidth is acceptable at the per-200ms cadence we use.
  elements: readonly unknown[];
};


export function WhiteboardPanel({ sessionId, bus, canSave, onClose, onSaved }: Props) {
  const qc = useQueryClient();
  const { t } = useTranslation();
  const apiRef = useRef<ExcalidrawImperativeAPI | null>(null);
  const originId = useMemo(
    () =>
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : Math.random().toString(36).slice(2),
    [],
  );
  const broadcastTimerRef = useRef<number | null>(null);
  const inboundRef = useRef(false);

  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // Phase 1B-I — track element count so the Save button can be
  // disabled when the canvas is empty. Without this, the operator
  // could fire a save with zero elements and burn an Evidence row +
  // chat card + audit entry for nothing. Both the operator's local
  // draws and inbound patches from the field bump this counter.
  const [elementCount, setElementCount] = useState(0);

  // Inbound: subscribe to scene patches.
  useEffect(() => {
    if (!bus) return;
    const off = bus.on<Patch>("whiteboard/scene.patch", (env) => {
      const p = env.payload;
      if (!p || p.origin === originId) return;
      const apiInstance = apiRef.current;
      if (!apiInstance) return;
      inboundRef.current = true;
      try {
        apiInstance.updateScene({ elements: p.elements as never });
      } finally {
        // Excalidraw fires onChange synchronously after updateScene;
        // clear the flag once we're past the callback.
        queueMicrotask(() => {
          inboundRef.current = false;
        });
      }
    });
    return () => off();
  }, [bus, originId]);

  // Open/close broadcast — operator's panel announces itself so the
  // field-side WhiteboardAutoOpen listener can mount its own
  // read-only panel. canSave-side is the operator side; the
  // field-side panel does NOT re-broadcast (or you get a feedback
  // loop). Closing the operator panel emits whiteboard/closed so
  // the field-side panel also dismisses.
  useEffect(() => {
    if (!bus || !canSave) return;
    void bus
      .send({
        topic: "whiteboard",
        type: "opened",
        payload: { origin: originId },
        reliable: true,
      })
      .catch(() => {});
    return () => {
      void bus
        .send({
          topic: "whiteboard",
          type: "closed",
          payload: { origin: originId },
          reliable: true,
        })
        .catch(() => {});
    };
  }, [bus, canSave, originId]);

  // Outbound: debounce-broadcast on local changes.
  const broadcastNow = (elements: readonly unknown[]) => {
    if (!bus) return;
    void bus
      .send<Patch>({
        topic: "whiteboard",
        type: "scene.patch",
        payload: { origin: originId, elements },
        reliable: false,
      })
      .catch(() => {
        /* best-effort; the next patch will catch the receiver up */
      });
  };

  const handleChange = (elements: readonly unknown[]) => {
    // Track for empty-canvas Save gating. Update for BOTH outbound
    // (local) and inbound (peer) changes so the operator can save a
    // whiteboard the field user drew on.
    setElementCount(elements.length);
    if (inboundRef.current) return;  // don't re-broadcast inbound updates
    if (broadcastTimerRef.current !== null) {
      clearTimeout(broadcastTimerRef.current);
    }
    broadcastTimerRef.current = window.setTimeout(() => {
      broadcastNow(elements);
      broadcastTimerRef.current = null;
    }, 150);
  };

  const save = useMutation({
    mutationFn: async () => {
      const apiInstance = apiRef.current;
      if (!apiInstance) throw new Error("whiteboard not ready");
      const elements = apiInstance.getSceneElements();
      const appState = apiInstance.getAppState();
      const blob = await exportToBlob({
        elements,
        appState,
        mimeType: "image/png",
        files: apiInstance.getFiles(),
      });
      const pngB64 = await blobToBase64(blob);
      const state = {
        elements,
        // Strip the transient bits Excalidraw stores in appState that
        // are runtime-only (collaborators, file ids, view bbox); we
        // keep just the layout-affecting ones so verification is
        // deterministic.
        appState: {
          viewBackgroundColor: appState.viewBackgroundColor,
          gridSize: appState.gridSize,
          theme: appState.theme,
        },
      };
      const res = await api.post(ApiPaths.sessionWhiteboardSave(sessionId), {
        png_b64: pngB64,
        state,
      });
      return res.data;
    },
    onSuccess: (data) => {
      setSavedAt(formatTime(Date.now()));
      setErr(null);
      void qc.invalidateQueries({
        queryKey: ["session-evidence", sessionId],
      });
      void qc.invalidateQueries({ queryKey: ["session-chat", sessionId] });
      // Best-effort feedback over the bus so the field side sees the save.
      const savedId = (data as { id: string }).id;
      bus?.send({
        topic: "whiteboard",
        type: "saved",
        payload: { evidence_id: savedId },
        reliable: true,
      }).catch(() => {});
      onSaved?.(savedId);
    },
    onError: (e) => {
      setErr((e as Error).message);
    },
  });

  const initial: ExcalidrawInitialDataState = useMemo(
    () => ({
      appState: { viewBackgroundColor: "#FFFFFF" },
    }),
    [],
  );

  // Portal to document.body — LiveKit's room wrapper sets `transform`
  // on its container, which would otherwise scope this panel's
  // `position: fixed` to that wrapper instead of the viewport (CSS
  // containing-block rule). The result was the panel only covering
  // the LiveKit video area + overlapping the TopBar + sidebar.
  // Portaling escapes any ancestor containing block.
  const target =
    typeof document !== "undefined" ? document.body : null;
  if (!target) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex flex-col bg-slate-900/95 backdrop-blur"
      data-testid="whiteboard-panel"
    >
      <header className="flex shrink-0 items-center justify-between gap-2 border-b border-nb-border bg-slate-900 px-3 py-2 sm:px-4">
        <div className="flex min-w-0 items-center gap-2 sm:gap-3">
          <button
            type="button"
            className="btn-ghost flex shrink-0 items-center gap-1.5 px-2 py-1 text-sm"
            onClick={onClose}
            data-testid="whiteboard-close"
            aria-label={t("whiteboard.close")}
          >
            <span aria-hidden>✕</span>
            <span className="hidden sm:inline">{t("whiteboard.close")}</span>
          </button>
          <h2 className="shrink-0 text-sm font-semibold text-nb-text">
            {t("whiteboard.title")}
          </h2>
          {/* Collab / offline / saved / err — single status pill, hidden
              on narrow viewports to keep the field-side header
              single-line. Operator desktop has plenty of room. */}
          <span className="hidden min-w-0 truncate text-xs sm:inline">
            {err ? (
              <span className="text-cvp-danger" role="alert">
                {err}
              </span>
            ) : savedAt ? (
              <span className="text-emerald-400" role="status">
                {t("whiteboard.savedAt", { time: savedAt })}
              </span>
            ) : bus ? (
              <span className="text-nb-text-3">
                {t("whiteboard.collabOn")}
              </span>
            ) : (
              <span className="text-cvp-warn">
                {t("whiteboard.offline")}
              </span>
            )}
          </span>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {canSave && (
            <button
              type="button"
              className="btn-primary text-sm"
              onClick={() => save.mutate()}
              disabled={save.isPending || elementCount === 0}
              title={
                elementCount === 0
                  ? t("whiteboard.nothingToSave")
                  : undefined
              }
              data-testid="whiteboard-save"
            >
              {save.isPending
                ? t("common.saving")
                : t("session.saveToEvidence")}
            </button>
          )}
        </div>
      </header>
      <div className="flex-1 min-h-0">
        <Excalidraw
          excalidrawAPI={(apiInstance) => {
            apiRef.current = apiInstance;
          }}
          initialData={initial}
          onChange={handleChange as never}
          viewModeEnabled={!canSave}
        />
      </div>
    </div>,
    target,
  );
}


async function blobToBase64(blob: Blob): Promise<string> {
  const buf = await blob.arrayBuffer();
  const bytes = new Uint8Array(buf);
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i] as number);
  }
  return btoa(binary);
}
